/**
 * Utility functions for embed mode detection and management
 */

/**
 * Check if the current page should be in embed mode
 * This checks for:
 * 1. /embed/ in the URL path
 * 2. ?embed=true query parameter
 * 3. m. subdomain (mobile/embed subdomain)
 * 4. embed. subdomain
 */
export function isEmbedMode(): boolean {
  const url = window.location;

  // Check URL path for /embed/
  if (url.pathname.includes("/embed/")) {
    return true;
  }

  // Check query parameter
  const params = new URLSearchParams(url.search);
  if (params.get("embed") === "true") {
    return true;
  }

  // Check subdomain
  const hostname = url.hostname;
  if (hostname.startsWith("m.") || hostname.startsWith("embed.")) {
    return true;
  }

  return false;
}

/**
 * Get the clean path without embed prefix
 * /embed/call-sms -> /call-sms
 */
export function getCleanPath(pathname: string): string {
  return pathname.replace("/embed", "");
}

/**
 * Convert a normal URL to an embed URL
 */
export function toEmbedUrl(url: string): string {
  try {
    const urlObj = new URL(url);

    // If already embed, return as-is
    if (urlObj.pathname.includes("/embed/")) {
      return url;
    }

    // Add /embed prefix to path
    urlObj.pathname = "/embed" + urlObj.pathname;
    return urlObj.toString();
  } catch {
    // Fallback for relative URLs
    return url.startsWith("/") ? `/embed${url}` : `/embed/${url}`;
  }
}

/**
 * Convert an embed URL to a normal URL
 */
export function fromEmbedUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    urlObj.pathname = getCleanPath(urlObj.pathname);
    return urlObj.toString();
  } catch {
    // Fallback for relative URLs
    return url.replace("/embed", "");
  }
}

/**
 * Get the appropriate path for navigation based on embed mode
 * This is useful for buttons/links that should navigate within embed mode
 */
export function getEmbedAwarePath(path: string): string {
  if (isEmbedMode()) {
    // If path already starts with /embed, return as-is
    if (path.startsWith("/embed/")) {
      return path;
    }
    // Otherwise add /embed prefix
    return `/embed${path}`;
  }
  return path;
}
